import type { Markings } from 'chessboard/plugins/markings/config/types';
import { KeyModifiers } from 'chessboard/model/dom/types';
import { stringArrayToObject } from 'utils/array/string-array-to-object';
import type JCE from '@chesscom/js-chess-engine';
import { convertStringToType } from './parse-effects';

export const enum JCEColorCodes {
  Alt = 'a',
  Ctrl = 'l',
  Custom = 'c',
  Default = 'd',
  Shift = 's',
}

/**
 * Convert from a JCE marking to a chessboard marking.
 */
export function convertMarkingFromJCE(marking: JCE.Marking.Model): Markings.KeyedModel {
  const { color, data = [], type } = marking;
  const dataObject = stringArrayToObject(data);

  // decode data
  Object.keys(dataObject).forEach((key) => {
    dataObject[key] = convertStringToType(decodeURIComponent(dataObject[key]));
  });

  const { persistent, ...rest } = dataObject;

  const newMarking: Markings.KeyedModel = {
    key: fromJCEKey(marking.key, marking.type),
    node: true,
    data: {
      ...rest,
      keyPressed: rest.keyPressed ?? fromJCEColor(color as JCEColorCodes),
    },
    type,
  };

  if (typeof persistent !== 'undefined') {
    newMarking.persistent = persistent;
  }

  return newMarking;
}

/**
 * Get key from a JCE key.
 */
function fromJCEKey(key: string, type: string) {
  return `${type}|${key}`;
}

/**
 * Convert from a JCE color to a chessboard color.
 */
function fromJCEColor(color?: JCEColorCodes) {
  switch (color) {
    case JCEColorCodes.Alt:
      return KeyModifiers.Alt;
    case JCEColorCodes.Default:
      return KeyModifiers.None;
    case JCEColorCodes.Shift:
      return KeyModifiers.Shift;
    case JCEColorCodes.Ctrl:
      return KeyModifiers.Ctrl;
    default:
      return undefined;
  }
}
